import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import {
  Box,
  Typography,
  Paper,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Chip,
  CircularProgress,
  TextField,
  Button,
  alpha,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { exportToExcel } from "../../../helpers/exportToExcel";
import { removeIdKeysFromData } from "../../../helpers/removeIdKeysFromData";
import InvoicePopup from "../InvoicePopup/InvoicePopup";
import UpdateRentPopup from "../UpdateRentPopup/UpdateRentPopup";
import ConfirmActionButton from "../ConfirmActionButton/ConfirmActionButton";

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
};

const formatDate = (date: string) => {
  return new Date(date).toLocaleDateString();
};

export const fetchRents = async (
  token: string | undefined | null,
  areActive: boolean,
) => {
  const { data } = await axios.get(`${apiVariable}/getAllRents`, {
    headers: { Authorization: `Bearer ${token}` },
    params: { areActive },
  });
  return data;
};

const RentTable: React.FC = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [areActive, setAreActive] = useState(true);

  const {
    data: rents,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["rents", areActive],
    queryFn: () => fetchRents(token, areActive),
    enabled: !!token,
  });

  const filteredRents = useMemo(() => {
    if (!rents) return [];
    return rents.filter((rent: any) =>
      [
        rent.unitNumber,
        rent.authorityCompanyName,
        rent.billToCompanyName,
        rent.soldToCompanyName,
        rent.driverFullName,
      ].some((field) =>
        field?.toString().toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    );
  }, [rents, searchTerm]);

  const filteredRentsWithoutIds = removeIdKeysFromData(filteredRents);

  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAreActive(event.target.checked);
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height={400}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl">
      <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
        <Box
          className="table-controls"
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mb: 3,
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={areActive}
                  onChange={handleSwitchChange}
                  color="primary"
                />
              }
              label={`${areActive ? "Active" : "Inactive"} Rents`}
            />
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="Search"
              variant="outlined"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by Unit, Authority, Driver..."
              size="small"
            />
            <Button
              onClick={() => exportToExcel(filteredRentsWithoutIds, "Rents")}
              disabled={filteredRents.length === 0}
              variant="outlined"
            >
              Export to Excel
            </Button>
          </Box>
        </Box>

        {isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {(error as any).message}
          </Alert>
        )}

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Unit No</TableCell>
                <TableCell>Asset Type</TableCell>
                <TableCell>Transaction Type</TableCell>
                <TableCell>Driver</TableCell>
                <TableCell>Authority Company</TableCell>
                <TableCell>Bill To</TableCell>
                <TableCell>Sold To</TableCell>
                <TableCell>Rental Period</TableCell>
                <TableCell>Billing Details</TableCell>
                <TableCell>Payment Details</TableCell>
                <TableCell>Mileage</TableCell>
                <TableCell>Dates</TableCell>
                <TableCell>Balance</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRents.map((rent: any) => (
                <TableRow
                  key={rent.id}
                  sx={{
                    "&:hover": {
                      backgroundColor: (theme) =>
                        alpha(theme.palette.primary.main, 0.04),
                    },
                  }}
                >
                  <TableCell
                    sx={{
                      cursor: "pointer",
                      color: "primary.main",
                      "&:hover": {
                        textDecoration: "underline",
                      },
                    }}
                    onClick={() => {
                      if (rent.truckId) {
                        navigate(`/fleet/assets/truck/${rent.truckId}`);
                      } else if (rent.trailerId) {
                        navigate(`/fleet/assets/trailer/${rent.trailerId}`);
                      }
                    }}
                  >
                    {rent.unitNumber}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={rent.assetType}
                      color={rent.truckId ? "primary" : "secondary"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{rent.transactionType}</TableCell>
                  <TableCell>{rent.driverFullName}</TableCell>
                  <TableCell>{rent.authorityCompanyName}</TableCell>
                  <TableCell>{rent.billToCompanyName}</TableCell>
                  <TableCell>{rent.soldToCompanyName || "-"}</TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">
                        Rental: {rent.rentalTerm}{" "}
                        {rent.billingFrequency === "Weekly"
                          ? "weeks"
                          : rent.billingFrequency === "Monthly"
                            ? "months"
                            : "years"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Lease: {rent.leaseTerm} months
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">
                        {rent.billingFrequency}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Base Rate: {formatCurrency(rent.baseRentalRate)}/
                        {rent.billingFrequency === "Weekly"
                          ? "week"
                          : rent.billingFrequency === "Monthly"
                            ? "month"
                            : "year"}
                      </Typography>
                      {rent.mileageRate > 0 && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          Mileage Rate: {formatCurrency(rent.mileageRate)}/mi
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">
                        Base: {formatCurrency(rent.baseRentalRate)}/
                        {rent.billingFrequency === "Weekly"
                          ? "week"
                          : rent.billingFrequency === "Monthly"
                            ? "month"
                            : "year"}
                      </Typography>
                      {rent.downPayment > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          Reimbursable deposit:{" "}
                          {formatCurrency(rent.downPayment)}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {rent.startMileage !== undefined && (
                      <Box>
                        <Typography variant="body2">
                          Start: {rent.startMileage?.toLocaleString() || 0} mi
                        </Typography>
                        {rent.endMileage !== null &&
                          rent.endMileage !== undefined && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              End: {rent.endMileage.toLocaleString()} mi
                            </Typography>
                          )}
                        {rent.mileageCaps !== null &&
                          rent.mileageCaps !== undefined && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                            >
                              Cap: {formatCurrency(rent.mileageCaps)}
                            </Typography>
                          )}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">
                        Start: {formatDate(rent.startDate)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        End: {rent.endDate ? formatDate(rent.endDate) : "N/A"}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      color={rent.remainingBalance > 0 ? "error" : "success"}
                    >
                      {formatCurrency(rent.remainingBalance)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <InvoicePopup rent={rent} />
                      <UpdateRentPopup rent={rent} />
                      <ConfirmActionButton
                        id={rent.id}
                        entityType="Rent"
                        actionType="delete"
                        entityLabel="rent"
                        queryKey="rents"
                        buttonLabel="Delete"
                        buttonColor="error"
                      />
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {filteredRents.length === 0 && (
                <TableRow>
                  <TableCell colSpan={14} align="center">
                    No rents found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Container>
  );
};

export default RentTable;
